/**
 * ExhibitionsList — Phase 1 tracker for local / domestic / international
 * exhibitions. Toggle between grid and calendar view. New button opens
 * a compact create dialog; heavy editing happens on the detail page.
 *
 * Feb 2026 · R15.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, MapPin, LayoutGrid, Plus, Building2, Users, Trophy, Sparkles, Globe2 } from "lucide-react";

const STATUS_COLORS = {
  planned: "border-neutral-400 text-neutral-600 bg-neutral-50",
  confirmed: "border-blue-400 text-blue-600 bg-blue-50",
  onsite: "border-emerald-400 text-emerald-600 bg-emerald-50",
  closed: "border-neutral-400 text-neutral-500 bg-neutral-100",
  cancelled: "border-rose-400 text-rose-600 bg-rose-50",
};

const YEARS = (() => {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1, y + 2];
})();

export default function ExhibitionsList() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("grid"); // grid | calendar
  const [year, setYear] = useState(new Date().getFullYear());
  const [status, setStatus] = useState("all");
  const [locType, setLocType] = useState("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (year) params.set("year", year);
      if (status !== "all") params.set("status", status);
      if (locType !== "all") params.set("location_type", locType);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/exhibitions?${params}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, year, status, locType, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4" data-testid="exhibitions-page">
      <PageHeader
        title="Exhibitions"
        description="Track local, domestic, and international exhibitions — booths, teams, budgets, footfall, and captured leads in one place."
        actions={
          <Button className="rounded-none h-9" onClick={() => setCreateOpen(true)} data-testid="exhibitions-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New exhibition
          </Button>
        }
      />

      <div className="flex items-center gap-2 flex-wrap border p-3">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-8 w-24 rounded-none text-xs" data-testid="exhibitions-year"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-36 rounded-none text-xs" data-testid="exhibitions-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={locType} onValueChange={setLocType}>
          <SelectTrigger className="h-8 w-36 rounded-none text-xs" data-testid="exhibitions-loctype"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            <SelectItem value="local">Local</SelectItem>
            <SelectItem value="domestic">Domestic</SelectItem>
            <SelectItem value="international">International</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Search name, city, venue…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-56 rounded-none text-xs" data-testid="exhibitions-search" />
        <div className="flex-1" />
        <div className="flex items-center border">
          <button onClick={() => setView("grid")} className={`px-3 h-8 text-xs flex items-center gap-1 ${view === "grid" ? "bg-primary text-primary-foreground" : ""}`} data-testid="exhibitions-view-grid"><LayoutGrid className="w-3 h-3" /> Grid</button>
          <button onClick={() => setView("calendar")} className={`px-3 h-8 text-xs flex items-center gap-1 border-l ${view === "calendar" ? "bg-primary text-primary-foreground" : ""}`} data-testid="exhibitions-view-calendar"><CalendarDays className="w-3 h-3" /> Calendar</button>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="border-2 border-dashed p-10 text-center space-y-2">
          <Building2 className="w-10 h-10 text-neutral-400 mx-auto" />
          <div className="font-medium">No exhibitions match these filters</div>
          <div className="text-sm text-muted-foreground">Track upcoming expos, capture leads onsite, and measure ROI.</div>
          <Button className="rounded-none" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Create your first exhibition
          </Button>
        </div>
      )}

      {view === "grid" && rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(r => (
            <ExhibitionCard key={r.id} r={r} onOpen={() => navigate(`/app/exhibitions/${r.id}`)} />
          ))}
        </div>
      )}

      {view === "calendar" && rows.length > 0 && (
        <CalendarView rows={rows} year={year} onOpen={(id) => navigate(`/app/exhibitions/${id}`)} />
      )}

      {createOpen && (
        <CreateExhibitionDialog
          wsId={activeWorkspaceId}
          onClose={() => setCreateOpen(false)}
          onDone={(newRow) => {
            setCreateOpen(false);
            if (newRow?.id) navigate(`/app/exhibitions/${newRow.id}`);
          }}
        />
      )}
    </div>
  );
}

function ExhibitionCard({ r, onOpen }) {
  const statusCls = STATUS_COLORS[r.status] || STATUS_COLORS.planned;
  return (
    <button onClick={onOpen} className="border p-3 space-y-2 hover:border-primary transition-colors text-left" data-testid={`exhibition-card-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            {r.location_type === "international" ? <Globe2 className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
            {r.location_type} · {r.city || "—"}{r.country_code && ` · ${r.country_code.toUpperCase()}`}
          </div>
          <div className="font-semibold truncate">{r.name}</div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 border ${statusCls}`}>{r.status}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {r.start_date} → {r.end_date}
        {r.venue_name && <span> · {r.venue_name}</span>}
      </div>
      <div className="grid grid-cols-3 gap-2 pt-1 text-center">
        <Stat icon={<Building2 className="w-3 h-3" />} label="Booth" value={r.booth?.number || "—"} />
        <Stat icon={<Users className="w-3 h-3" />} label="Team" value={(r.team || []).length} />
        <Stat icon={<Trophy className="w-3 h-3" />} label="Leads" value={r.captured_leads_count || 0} />
      </div>
    </button>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border p-1.5">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">{icon}{label}</div>
      <div className="font-mono text-sm font-semibold">{value}</div>
    </div>
  );
}

// ─────────────── Calendar view (12-month strip) ───────────────

function CalendarView({ rows, year, onOpen }) {
  const months = useMemo(() => {
    const grouped = {};
    for (let m = 0; m < 12; m++) grouped[m] = [];
    rows.forEach(r => {
      try {
        const d = new Date(r.start_date);
        if (d.getFullYear() === year) grouped[d.getMonth()].push(r);
      } catch { /* ignore */ }
    });
    return grouped;
  }, [rows, year]);
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="exhibitions-calendar">
      {monthNames.map((m, mi) => (
        <div key={mi} className="border">
          <div className="bg-neutral-50 px-2 py-1 text-xs font-semibold border-b">
            {m} {year} <span className="text-muted-foreground font-normal">· {months[mi].length}</span>
          </div>
          <div className="min-h-[80px] p-2 space-y-1">
            {months[mi].length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">No events</div>
            ) : months[mi].map(r => {
              const statusCls = STATUS_COLORS[r.status] || STATUS_COLORS.planned;
              return (
                <button key={r.id} onClick={() => onOpen(r.id)} className={`w-full text-left text-xs px-2 py-1 border ${statusCls} hover:border-primary`} data-testid={`exhibition-cal-${r.id}`}>
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="text-[10px] opacity-70">{r.start_date.slice(5)} — {r.end_date.slice(5)}</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────── Create dialog (compact) ───────────────

function CreateExhibitionDialog({ wsId, onClose, onDone }) {
  const [form, setForm] = useState({
    name: "", location_type: "domestic", city: "", country_code: "IN",
    venue_name: "", start_date: "", end_date: "",
    status: "planned",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (!form.start_date || !form.end_date) return toast.error("Dates required");
    if (form.end_date < form.start_date) return toast.error("End before start");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/exhibitions`, form);
      toast.success("Exhibition created");
      onDone?.(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> New exhibition</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 rounded-none" placeholder="e.g. TechExpo Delhi 2026" data-testid="exhibition-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Location type</Label>
              <Select value={form.location_type} onValueChange={v => setForm({ ...form, location_type: v })}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Local</SelectItem>
                  <SelectItem value="domestic">Domestic</SelectItem>
                  <SelectItem value="international">International</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label className="text-xs">City</Label>
              <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="h-9 rounded-none" />
            </div>
            <div>
              <Label className="text-xs">Country</Label>
              <Input maxLength={3} value={form.country_code} onChange={e => setForm({ ...form, country_code: e.target.value.toUpperCase() })} className="h-9 rounded-none font-mono uppercase" placeholder="IN" data-testid="exhibition-form-country" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Venue</Label>
            <Input value={form.venue_name} onChange={e => setForm({ ...form, venue_name: e.target.value })} className="h-9 rounded-none" placeholder="e.g. Pragati Maidan" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Start date *</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="h-9 rounded-none" data-testid="exhibition-form-start" />
            </div>
            <div>
              <Label className="text-xs">End date *</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="h-9 rounded-none" data-testid="exhibition-form-end" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="exhibition-form-submit">
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
